/**
 * Firebase / Google Analytics initialisation.
 *
 * Lives in a dedicated `FirebaseApp` instance (named `'gravitydash-analytics'`)
 * so the primary Auth `FirebaseApp` from `firebase.ts` doesn't need to know about
 * `measurementId`. That keeps the auth-side init untouched (and therefore
 * un-broken by this change), at the cost of one extra `FirebaseApp` object
 * per page — negligible memory, no functional impact.
 *
 * `getAnalytics(app)` reads `app.options.measurementId`; if missing it
 * throws. We pass it explicitly here so the analytics-only app always has
 * it, regardless of how the auth-side config is shaped.
 *
 * Behaviour:
 *   - No-op (returns null) when `VITE_FIREBASE_MEASUREMENT_ID` is unset.
 *     New devs without GA in their `.env` get a working app with no
 *     events fired — they opt in by adding the env var.
 *   - No-op when not in a browser (SSR / Node tests / typecheck).
 *   - No-op when `isSupported()` returns false (very old browsers, some
 *     WebViews, Safari with full ITP cookie blocking, etc.).
 *
 * Failed analytics REST calls (e.g. `*.google-analytics.com` blocked by
 * an ad-blocker) are silent and do not break the rest of the app — they
 * just won't be tracked.
 */

import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';

// Internal Firebase app instance identifier — kept stable (not rebranded)
// to match the auth instances in `firebase.ts`; see the note there.
const ANALYTICS_APP_NAME = 'gravitydash-analytics';

interface AnalyticsConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  measurementId: string;
}

function readConfig(): AnalyticsConfig | null {
  const env = import.meta.env as Record<string, string | undefined>;
  const apiKey = env.VITE_FIREBASE_API_KEY;
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = env.VITE_FIREBASE_PROJECT_ID;
  const appId = env.VITE_FIREBASE_APP_ID;
  const measurementId = env.VITE_FIREBASE_MEASUREMENT_ID;
  if (!apiKey || !authDomain || !projectId || !appId || !measurementId) {
    return null;
  }
  return { apiKey, authDomain, projectId, appId, measurementId };
}

let initPromise: Promise<Analytics | null> | null = null;

export function initFirebaseAnalytics(): Promise<Analytics | null> {
  if (!initPromise) {
    initPromise = doInit();
  }
  return initPromise;
}

async function doInit(): Promise<Analytics | null> {
  if (typeof window === 'undefined') {
    return null;
  }
  const cfg = readConfig();
  if (!cfg) {
    return null;
  }
  if (!(await isSupported())) {
    return null;
  }
  const existing = getApps().find((a) => a.name === ANALYTICS_APP_NAME);
  const app: FirebaseApp = existing ?? initializeApp(cfg, ANALYTICS_APP_NAME);
  return getAnalytics(app);
}
